import type { VideoJsPlayer as IPixellotPlayer } from "@pixellot/web-sdk";
import type { IPlayerState } from "@pixellot/vue-player/src/util/state";
import EventsAPI from "~/services/events";
import TagsAPI from "~/services/tags";
import useUserStore from "~/stores/user";
import SubscriptionAPI from "~/services/subscription";
import { ORDER, SORT } from "~/constants/request_params";
import { COMMERCE_CATEGORY } from "~/constants/commerce";

const useEventStore = defineStore("event", {
  state: () => ({
    data: null as null | IModelEvent,
    commerceOptions: [] as IModelCommerce[],
    hasGeniusError: false,
    isEventStartingSoon: false,
    loading: false,
    tags: {
      data: new Map() as Map<string, IModelTag>,
      loading: false,
      activeEditTag: null as null | IModelTag,
      isCreatingTag: false,
      isEditingTag: false,
      error: null as null | string,
    },
    isCreatingClip: false,
    canReloadSuggested: false,
    userClips: [] as IModelEvent[],
    userHighlights: [] as IModelEvent[],
    gameHighlights: [] as IModelEvent[],
    scoreStats: null as null | IEventScoreStats,
    gameStats: null as null | IEventGameStats,
    eventError: null as null | string,
    player: null as null | IPixellotPlayer,
    playerState: null as null | IPlayerState,
  }),

  getters: {
    activePPVTicket: (state) => {
      const userStore = useUserStore();

      return userStore.ppvTickets.find(t => state.commerceOptions.find(c => t.eventId === c.eventId));
    },
  },

  actions: {
    async load(id: string, type?: TEventType) {
      const userStore = useUserStore();
      const appConfig = useRuntimeConfig().public;
      const isEnableMatchStats = appConfig.APP_CUSTOMER.enable_match_stats;

      this.loading = true;

      this.data = await this.loadEvent(id, type);
      if (!this.data) {
        showError({ statusCode: 404, data: { context: "event" } });
        return;
      }

      const eventId = this.data.eventId;
      const referenceId = this.data.referenceId;
      const { commerceCategory } = this.data;
      const isCommerce = (
        commerceCategory === COMMERCE_CATEGORY.PPV
        || commerceCategory === COMMERCE_CATEGORY.PPV_SUBSCRIPTION
        || commerceCategory === COMMERCE_CATEGORY.SUBSCRIPTION
      );

      const loadClipsPromise = eventId && userStore.isAuthenticated ? this.loadUserMedia(eventId, "clips") : Promise.resolve([]);
      const loadHighlightsPromise = eventId && userStore.isAuthenticated ? this.loadUserMedia(eventId, "highlight") : Promise.resolve([]);
      const loadStatsPromise = eventId && isEnableMatchStats ? this.loadStats(eventId) : Promise.resolve({ scores: null, overview: null });
      const loadTagsPromise = this.loadTags(referenceId, this.data);
      const loadGameHighlightsPromise = eventId ? this.loadGameHighlights(eventId) : Promise.resolve([]);
      const loadCommerceOptionsPromise = isCommerce ? this.loadCommerceOptions(this.data) : Promise.resolve([]);

      const [userClips, userHighlights, loadedTags, gameHighlights, commerceOptions, stats] = await Promise.all([
        loadClipsPromise,
        loadHighlightsPromise,
        loadTagsPromise,
        loadGameHighlightsPromise,
        loadCommerceOptionsPromise,
        loadStatsPromise,
      ]);

      this.userClips = userClips;
      this.userHighlights = userHighlights;
      this.tags.data = new Map(loadedTags.map(tag => [tag.id, tag]));
      this.gameHighlights = gameHighlights;
      this.commerceOptions = commerceOptions;
      this.scoreStats = stats.scores;
      this.gameStats = stats.overview;

      this.canReloadSuggested = false;
      this.loading = false;
    },
    async loadEvent(id: string, type?: TEventType) {
      let event = null;
      try {
        event = await EventsAPI.loadById(id, type);
      } catch (err) {
        if (err instanceof Error)
          this.eventError = err.message;
        else this.eventError = String(err);
      }
      return event;
    },
    async loadTags(id: string, event: IModelEvent) {
      const userStore = useUserStore();
      let tags: IModelTag[] = [];
      try {
        if (userStore.isAuthenticated) {
          // TODO: Move out global tags when BE will remove authentication from there
          const [globalTags, userTags] = await Promise.all([
            TagsAPI.load({ id, event, global: true }),
            TagsAPI.load({ id, event, global: false }),
          ]);

          tags = tags.concat(userTags, globalTags);
        }
      } catch (err) {
        if (err instanceof Error)
          this.tags.error = err.message;
        else this.tags.error = String(err);
      }
      return tags;
    },
    loadUserMedia(
      eventId: string,
      user_media_type: "clips" | "highlight",
    ): Promise<IModelEvent[]> {
      return EventsAPI.userMedia
        .load({ page: 0, pageSize: 24, order: ORDER.desc, sort: SORT.added, eventId, user_media_type })
        .then(response => response.content.entries)
        .catch((error) => {
          console.error(`Failed to load User ${user_media_type}`, error);
          return [];
        });
    },
    loadGameHighlights(eventId: string): Promise<IModelEvent[]> {
      return EventsAPI.userMedia
        .loadGameHighlights({ page: 0, pageSize: 24, order: ORDER.desc, sort: SORT.added, eventId, contentType: "highlight" })
        .then(response => response.content.entries)
        .catch((error) => {
          console.error("Failed to load Game Highlights", error);
          return [];
        });
    },
    loadStats(eventId: string): Promise<IEventStats> {
      return EventsAPI.stats
        .getEventScoreStatsById(eventId)
        .catch((error) => {
          console.error("Failed to load Score Stats", error);
          return { scores: null, overview: null };
        });
    },
    loadCommerceOptions(event: IModelEvent) {
      const id = (event.eventType === "event" || event.eventType === "clip") ? event.id : event.eventId;

      if (!id)
        return [];

      return SubscriptionAPI
        .loadVodCommerceOptions(id)
        .catch((error) => {
          console.error("Failed to load commerce Options", error);
          return [];
        });
    },
    reset(): void {
      this.data = null;
      this.hasGeniusError = false;
      this.eventError = null;
      this.loading = false;
      this.isEventStartingSoon = false;
      this.tags.data = new Map() as Map<string, IModelTag>;
      this.tags.loading = false;
      this.tags.activeEditTag = null as null | IModelTag;
      this.tags.isCreatingTag = false;
      this.tags.isEditingTag = false;
      this.tags.error = null as null | string;
      this.isCreatingClip = false;
      this.userClips = [];
      this.userHighlights = [];
      this.gameHighlights = [];
      this.commerceOptions = [];
      this.scoreStats = null;
      this.gameStats = null;
      this.player = null;
      this.playerState = null;
    },
  },
});

export default useEventStore;

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useEventStore, import.meta.hot));
